/**
 * Builds role-group tabs from interface elements + GET /roles.
 */

import * as React from 'react';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { useRoles } from '@/hooks/useRoles';
import { toRoleColorCss } from '@/utils/role-label-utils';

/** One role-group tab (id, label, icon, branding color). */
export interface RoleGroupTab {
  id: string;
  label: string;
  icon: string;
  color: string | null;
}

/** Tab bar state returned by {@link useRoleGroupTabs}. */
export interface UseRoleGroupTabsReturn {
  visibleTabs: RoleGroupTab[];
  showTabBar: boolean;
  activeRoleFilter: string;
  setActiveRoleFilter: (role: string) => void;
  enabledRoles: string[];
  roleByCode: Record<string, { icon?: string | null; color?: string | null; label: string }>;
}

/**
 * Shared hook that drives tab navigation for all role group management pages.
 *
 * Reads tab IE children of `menuId` inside `interfaceName`, resolves icon/color
 * from the live roles API, and manages the active tab state.
 *
 * Used by {@link DynamicRoleGroupManagementPage}, {@link TutorsManagementPage}, {@link VolunteersManagementPage}.
 */
export function useRoleGroupTabs(interfaceName: string, menuId: string): UseRoleGroupTabsReturn {
  // `elements` already drops visible === false (same rule as create/edit actions).
  const { elements: pageElements = [] } = useInterfaceElements(interfaceName);
  const { data: allRoles = [], isSuccess: rolesLoaded, isError: rolesError } = useRoles();
  const [activeRoleFilter, setActiveRoleFilter] = React.useState<string>('all');

  const roleByCode = React.useMemo(
    () => Object.fromEntries(allRoles.map((r) => [r.code.toLowerCase(), r])),
    [allRoles],
  );

  const visibleTabs = React.useMemo<RoleGroupTab[]>(() => {
    const tabs = pageElements.filter((el) => el.elementType === 'tab');
    return [...tabs]
      .sort((a, b) => (a.userOrder ?? a.order ?? Infinity) - (b.userOrder ?? b.order ?? Infinity))
      .flatMap((el) => {
        const targetRole = el.config?.targetRole;
        const roleCode =
          typeof targetRole === 'string' && targetRole
            ? targetRole
            : el.elementId.replace('tab-', '').replaceAll('-', '_');
        const role = roleByCode[roleCode];

        // Once roles are loaded, drop tabs for roles that no longer exist in DB.
        // Guard on allRoles.length > 0 to handle 403 (roles not accessible): in that
        // case we keep all tabs to avoid hiding content from non-super-admins.
        if (rolesLoaded && allRoles.length > 0 && !role) return [];

        return [
          {
            id: roleCode,
            label: el.elementLabel,
            icon: role?.icon ?? (el.config?.icon as string | undefined) ?? 'Users',
            color: role?.color ? toRoleColorCss(role.color) : null,
          },
        ];
      });
  }, [pageElements, menuId, roleByCode, rolesLoaded, allRoles.length]);

  // Don't show tabs until roles API has settled (success OR error/403).
  // Without this guard, outdated IE tab entries appear briefly while roles are loading.
  const rolesReady = rolesLoaded || rolesError;
  const showTabBar = rolesReady && visibleTabs.length > 1;

  React.useEffect(() => {
    if (!rolesReady) return;
    if (visibleTabs.length === 1) {
      setActiveRoleFilter(visibleTabs[0].id);
    } else if (visibleTabs.length > 1 && !visibleTabs.some((t) => t.id === activeRoleFilter)) {
      setActiveRoleFilter('all');
    }
  }, [rolesReady, visibleTabs, activeRoleFilter]);

  const enabledRoles = React.useMemo<string[]>(() => {
    if (activeRoleFilter === 'all') return visibleTabs.map((t) => t.id);
    return [activeRoleFilter];
  }, [activeRoleFilter, visibleTabs]);

  return {
    visibleTabs,
    showTabBar,
    activeRoleFilter,
    setActiveRoleFilter,
    enabledRoles,
    roleByCode,
  };
}
